import {faPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import {useCreateButton, useGo} from '@refinedev/core';
import type {CreateButtonProps} from '@refinedev/mui';
import React, {FC} from 'react';

/**
 * <CreateButton> uses Material UI {@link https://mui.com/components/buttons/ `<Button> component`}.
 * It uses the {@link https://refine.dev/docs/api-reference/core/hooks/navigation/useNavigation#create `create`} method from {@link https://refine.dev/docs/api-reference/core/hooks/navigation/useNavigation `useNavigation`} under the hood.
 * It can be useful to redirect the app to the create page route of resource}.
 *
 * @see {@link https://refine.dev/docs/api-reference/mui/components/buttons/create-button} for more details.
 */
export const CreateButton: FC<CreateButtonProps> = ({
  resource: resourceNameFromProps,
  resourceNameOrRouteName,
  hideText = false,
  accessControl,
  meta,
  children,
  onClick,
  ...props
}) => {
  const go = useGo();

  const {label, title, disabled, hidden, to, LinkComponent} = useCreateButton({
    resource: resourceNameFromProps ?? resourceNameOrRouteName,
    meta,
    accessControl,
  });
  const isDisabled = disabled || props.disabled;
  const isHidden = hidden || props.hidden;

  if (isHidden) return null;

  const {sx, ...restProps} = props;

  return (
    <LinkComponent
      to={to}
      replace={false}
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        if (onClick) {
          e.preventDefault();
          onClick(e);
        }
      }}
      style={{textDecoration: 'none'}}
    >
      <Button
        disabled={isDisabled}
        startIcon={!hideText && <FontAwesomeIcon icon={faPlus} />}
        title={title}
        variant="soft"
        color="success"
        sx={{minWidth: 0, ...sx}}
        {...restProps}
      >
        {hideText ? <FontAwesomeIcon icon={faPlus} size="lg" /> : (children ?? label)}
      </Button>
    </LinkComponent>
  );
};
